/**
 * Qué es "nuevo" en el panel de turnos.
 *
 * El cartelito de "Nuevo" salía por una ventana fija de 12 horas desde que se
 * reservó. Eso tiene dos problemas, y el segundo es el que lo volvía inútil:
 *
 *   1. No sabe si ya lo viste. Mirás el panel a las 9, volvés a las 11, y lo
 *      que entró a las 2 de la mañana te lo sigue marcando como novedad aunque
 *      lo hayas visto dos horas antes.
 *   2. En un negocio con movimiento, casi todo entró en las últimas 12 horas.
 *      Un cartel que aparece en todas las filas no distingue nada: es ruido con
 *      forma de aviso, y se aprende a no verlo.
 *
 * "Nuevo" tiene que querer decir *llegó desde la última vez que miraste*. Eso
 * necesita recordar cuándo fue esa vez, y por eso hay un lado con localStorage
 * además de la decisión pura.
 *
 * La decisión es una función pura y separada del almacenamiento a propósito:
 * es lo único que hay que poder probar, y probarlo no debería pedir un
 * navegador.
 */

const HORA = 60 * 60 * 1000;
const DIA = 24 * HORA;

/**
 * Cuánto se considera nuevo cuando NO hay una visita anterior registrada.
 *
 * Es el primer ingreso, o el primero después de limpiar el navegador. Sin este
 * respaldo habría dos opciones y las dos malas: marcar todo como nuevo —que es
 * de donde venimos— o no marcar nada, y perderse los turnos que entraron
 * mientras tanto.
 */
export const HORAS_SIN_VISITA_PREVIA = 12;

/**
 * Tope duro: nada más viejo que esto es "nuevo", ni aunque nunca lo hayas
 * mirado.
 *
 * Alguien que no entra al panel en un mes no necesita que se le marquen como
 * novedad los turnos de hace tres semanas: a esa altura ya pasaron por mail y
 * por WhatsApp. Lo que necesita es ver lo de estos días.
 */
export const TOPE_DIAS = 7;

/**
 * ¿Este turno entró desde la última vez que se miró el panel?
 *
 * `ultimaVisita` en `null` significa "no hay registro", y ahí manda la ventana
 * de respaldo. Un `createdAt` en el futuro devuelve false: es reloj desfasado,
 * no una novedad.
 */
export function esTurnoNuevo(createdAt, { ultimaVisita = null, ahora = Date.now() } = {}) {
  if (!createdAt) return false;

  const cuando = typeof createdAt === 'number' ? createdAt : Date.parse(createdAt);
  if (!Number.isFinite(cuando)) return false;

  const transcurrido = ahora - cuando;
  if (transcurrido < 0) return false;
  if (transcurrido >= TOPE_DIAS * DIA) return false;

  const visto = typeof ultimaVisita === 'number' ? ultimaVisita : Date.parse(ultimaVisita);
  if (!Number.isFinite(visto)) {
    return transcurrido < HORAS_SIN_VISITA_PREVIA * HORA;
  }

  return cuando > visto;
}

/** Cuántos de estos turnos son nuevos. Para el contador del encabezado. */
export function contarNuevos(turnos, opciones) {
  return (Array.isArray(turnos) ? turnos : []).filter((t) => esTurnoNuevo(t?.createdAt, opciones))
    .length;
}

// ---------------------------------------------------------------------------
// El lado que recuerda. Va por negocio: dos cuentas en el mismo navegador no
// comparten "la última vez que miré".
// ---------------------------------------------------------------------------

const clave = (negocioId) => `aturno_ultima_visita_turnos_${negocioId || 'anonimo'}`;

/** Cuándo se miró el panel por última vez, o `null` si no hay registro. */
export function leerUltimaVisita(negocioId) {
  try {
    const guardado = localStorage.getItem(clave(negocioId));
    if (!guardado) return null;
    const instante = Number(guardado);
    return Number.isFinite(instante) ? instante : null;
  } catch (error) {
    // Modo incógnito, storage lleno o bloqueado por el navegador. Sin registro
    // se cae a la ventana de respaldo, que es un peor "nuevo" pero funciona.
    return null;
  }
}

/** Deja anotado que el panel se miró en este instante. */
export function marcarVisita(negocioId, instante = Date.now()) {
  try {
    localStorage.setItem(clave(negocioId), String(instante));
  } catch (error) {
    // Si no se puede guardar, el panel sigue andando: lo único que se pierde es
    // que la próxima vez marque de más.
  }
}
